import { useApi } from '@/api';
import {
  ActivityCard,
  EntityName,
  KpiCard,
  Link,
  LinkButton,
  Page,
  PageSkeleton
} from '@/components';
import { tokensMapping } from '@/utils/tokens';
import {
  CryptoIcon,
  Flex,
  Heading,
  LinkWrapper,
  CurrencyValue
} from '@blockanalitica/ui';
import { useParams } from 'react-router-dom';
import CollateralAtRiskTabs from './CollateralAtRiskTabs';
import HistoricTabs from './HistoricTabs';
import TopVaults from './TopVaults';
import VaultsDonutChart from './VaultsDonutChart';
export default function ILKPage() {
  const { ilk } = useParams();
  const [data, error] = useApi({
    url: `/groups/core/ilks/${ilk}/`,
    queryParams: {
      days_ago: 1
    }
  });

  if (error) {
    throw error;
  }

  if (data) {
    return (
      <Page
        title={
          <EntityName
            symbol1={<CryptoIcon name={data.collateral_symbol} size="5" />}>
            <Flex gap="3xs" flexDirection="column">
              <Flex gap="3xs">
                <Heading size="1">{data.name}</Heading>
              </Flex>
              <Flex gap="3xs" color="muted">
                {data.ilk}
              </Flex>
            </Flex>
          </EntityName>
        }>
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <KpiCard
            title="Total Debt"
            variant="dai"
            data={data}
            field="debt"
            options={{
              trend: true,
              valueOptions: {
                currencyValueOptions: {
                  currency: tokensMapping('DAI'),
                  iconSize: '3'
                }
              }
            }}
          />
          <KpiCard
            title="Total Collateral Locked"
            data={data}
            field="collateral"
            options={{
              valueOptions: {
                currencyValueOptions: {
                  currency: {
                    name: data.collateral_symbol,
                    address: data.collateral_address
                  },
                  iconSize: '3'
                }
              },
              variants: { kpiCardFooter: 'muted' }
            }}
          />
        </Flex>

        <HistoricTabs
          ilk={ilk}
          collateralSymbol={data.collateral_symbol}
          collateralAddress={data.collateral_address}
        />

        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <KpiCard
            title="Weighted Collateral Ratio"
            data={data}
            field="weighted_cr"
            options={{
              percentage: true,
              trend: true,
              variants: { valuePercentageValue: 'dynamic' }
            }}
          />
        </Flex>

        <Flex gap={['m', 's']} flexDirection="column">
          <Flex gap="s" justifyContent="space-between" alignItems="center">
            <Heading size="3">Parameters</Heading>
            <LinkButton
              size="small"
              to="parameters"
              variant="secondary"
              height="1.2rem">
              view all
            </LinkButton>
          </Flex>
          <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
            <LinkWrapper
              to="parameters/stability-fee"
              // @ts-ignore
              as={Link}
              variant="kpiCardLinkWithBlendMode">
              <KpiCard
                title="Stability Fee"
                data={data}
                field="duty"
                options={{
                  percentage: true,
                  trend: true,
                  variants: { valuePercentageValue: 'dynamic' },
                  linkArrow: true
                }}
              />
            </LinkWrapper>

            <KpiCard
              title="Liquidation Ratio"
              data={data}
              field="mat"
              options={{
                percentage: true,
                trend: true,
                variants: { valuePercentageValue: 'dynamic' }
              }}
            />
            <KpiCard
              title="Debt Ceiling"
              data={data}
              field="line"
              options={{
                currency: tokensMapping('DAI'),
                notation: 'compact',
                trend: true,
                variants: { valuePercentageValue: 'dynamic' }
              }}
            />
            <KpiCard
              title="Dust"
              data={data}
              field="dust"
              options={{
                currency: tokensMapping('DAI'),
                notation: 'compact',
                trend: true,
                variants: { valuePercentageValue: 'dynamic' }
              }}
            />
          </Flex>
          <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
            <KpiCard
              title="OSM Price"
              data={data}
              field="osm_price"
              options={{
                currency: 'USD'
              }}
            />
            <KpiCard
              title="Next OSM Price"
              data={data}
              field="next_price"
              options={{
                currency: 'USD'
              }}
            />
            <LinkWrapper
              to={`/liquidations/${ilk}/`}
              // @ts-ignore
              as={Link}
              variant="kpiCardLinkWithBlendMode">
              <KpiCard
                title="Liquidations"
                data={data}
                field="auctions"
                options={{
                  linkArrow: true
                }}
                footer={
                  <Flex gap="3xs">
                    Debt Repaid
                    <CurrencyValue
                      value={data.debt_liquidated}
                      options={{
                        currency: 'DAI',
                        iconSize: '-1'
                      }}
                    />
                  </Flex>
                }
              />
            </LinkWrapper>
          </Flex>
        </Flex>

        <CollateralAtRiskTabs ilk={ilk} />

        <ActivityCard
          url={`/groups/core/ilks/${ilk}/activity/`}
          linkTo="activity"
        />
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <Flex gap={['m', 's']} flexDirection="column" width="100%">
            <LinkWrapper
              to="cdps"
              // @ts-ignore
              as={Link}
              variant="kpiCardLinkWithBlendMode">
              <KpiCard
                title="N° of CDPs"
                data={data}
                field="num_urns"
                options={{
                  trend: true,
                  variants: { valuePercentageValue: 'dynamic' },
                  linkArrow: true
                }}
              />
            </LinkWrapper>
            <TopVaults ilk={ilk} />
          </Flex>

          <Flex flexBasis="100%" flexDirection="column" alignItems="center">
            <Heading size="4">CDPs Distribution</Heading>
            <VaultsDonutChart ilk={ilk} />
          </Flex>
        </Flex>
      </Page>
    );
  }

  return <PageSkeleton />;
}
